/**
 * SCWT-PM-V2 Phase 3 — PM Overview tab.
 * Onboarding checklist + local partner card + quick links.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, ArrowRight, ShieldCheck, MapPin, Mail, Phone } from "lucide-react";
import api from "@/lib/api";

export default function PmOverviewTab() {
  const [onboarding, setOnboarding] = useState(null);
  const [localPartner, setLocalPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ob, lp] = await Promise.all([
          api.get("/property-manager/onboarding"),
          api.get("/property-manager/local-partner"),
        ]);
        setOnboarding(ob.data);
        setLocalPartner(lp.data?.local_partner || null);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;
  }

  return (
    <div data-testid="pm-overview-tab" className="space-y-6">
      {/* Onboarding checklist */}
      <section data-testid="pm-onboarding-card" className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Get set up</h2>
            <p className="text-xs text-slate-500 mt-0.5">Four quick steps to activate your agency.</p>
          </div>
          <div data-testid="pm-onboarding-progress" className="text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-100 rounded-full px-3 py-1">
            {onboarding?.done_count || 0} of {onboarding?.total || 4} complete
          </div>
        </div>

        <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div data-testid="pm-onboarding-bar"
                className="h-full bg-cyan-600 transition-all"
                style={{ width: `${onboarding?.percent || 0}%` }} />
        </div>

        <ol className="mt-5 space-y-2">
          {(onboarding?.steps || []).map((step) => (
            <li key={step.key}
                data-testid={`pm-onboarding-step-${step.key}`}
                className={`flex items-center gap-3 rounded-xl p-3 border ${
                  step.done ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-200"
                }`}>
              {step.done
                ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                : <Circle className="w-5 h-5 text-slate-400 flex-shrink-0" />}
              <span className={`flex-1 text-sm ${step.done ? "text-emerald-800 line-through" : "text-slate-800 font-medium"}`}>
                {step.label}
              </span>
              {!step.done && step.cta_link && (
                <Link to={step.cta_link}
                      data-testid={`pm-onboarding-cta-${step.key}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900">
                  Start <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* Local Partner snapshot */}
      {localPartner && (
        <section data-testid="pm-overview-local-partner" className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-cyan-700">
            <ShieldCheck className="w-3.5 h-3.5" /> Your local PWS Partner
          </div>
          <div className="mt-3">
            <div className="font-semibold text-slate-900" data-testid="pm-local-partner-name">
              {localPartner.business_name}
            </div>
            {localPartner.region && (
              <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {localPartner.region}
              </div>
            )}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-700">
              {localPartner.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{localPartner.email}</span>}
              {localPartner.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{localPartner.phone}</span>}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
